/**
 * Civilian Nights — Pay & Reconcile attendee panel.
 *
 * Renders inside EventDetailsPage when the event's organiser is
 * collecting money manually (bank transfer / cash / PayPal). The bank
 * details endpoint is RSVP-gated on the backend, so this component
 * only attempts to fetch them once the viewer is "Going". The
 * "I've paid" dialog captures proof (date / time / reference / method)
 * so the organiser can reconcile from their attendees view.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PoundSterling, CheckCircle2, Clock, Banknote, Loader2, Lock, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_PILLS = {
  unpaid: { label: 'Unpaid', cls: 'bg-amber-500/20 text-amber-400 border-amber-400/40' },
  marked_paid: { label: 'Awaiting confirmation', cls: 'bg-blue-500/20 text-blue-300 border-blue-400/40' },
  received: { label: 'Paid · confirmed', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40' },
  refunded: { label: 'Refunded', cls: 'bg-rose-500/20 text-rose-300 border-rose-400/40' },
};

const authHeaders = () => {
  const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

function CopyRow({ label, value, testid }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed — long-press to copy');
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-left"
    >
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold truncate">{value}</p>
      </div>
      <Copy className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
    </button>
  );
}

export default function EventPayReconcilePanel({ event, imGoing, currentUser, onPaymentChanged }) {
  const eventId = event?.event_id;
  const [details, setDetails] = useState(null);
  const [statusRow, setStatusRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaidDialog, setShowPaidDialog] = useState(false);

  const isOrganiser = !!currentUser && event && currentUser.user_id === event.organiser_id;
  const canViewDetails = !!currentUser && (imGoing || isOrganiser);

  const refresh = useCallback(async () => {
    if (!eventId || !canViewDetails) return;
    setLoading(true);
    try {
      const [detailsRes, statusRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/payment-details`, { headers: authHeaders() }),
        axios.get(`${API}/events/${eventId}/payment-status/me`, { headers: authHeaders() }),
      ]);
      setDetails(detailsRes.data);
      setStatusRow(statusRes.data);
    } catch (err) {
      // Silent — the panel just stays in its previous state.
      // Most likely cause: token expiry handled by global interceptor.
    } finally {
      setLoading(false);
    }
  }, [eventId, canViewDetails]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!event?.organiser_collects_money) return null;

  // Locked card — viewer is not RSVP'd yet.
  if (!canViewDetails) {
    return (
      <div className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-4" data-testid="pay-reconcile-locked">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-amber-200">Payment details locked</p>
            <p className="text-xs text-amber-200/70 mt-0.5 leading-snug">
              Tap <span className="font-semibold">I&apos;m Going</span> above to unlock the organiser&apos;s bank details and mark yourself as paid.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const status = statusRow?.payment_status || 'unpaid';
  const pill = STATUS_PILLS[status] || STATUS_PILLS.unpaid;
  const bank = details?.details || {};
  const cashAccepted = !!details?.cash_accepted;
  const proof = statusRow?.payment_proof;

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 to-secondary/4 p-4 space-y-4" data-testid="pay-reconcile-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
            <PoundSterling className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold">How to pay</p>
            <p className="text-[11px] text-muted-foreground">Pay the organiser directly &amp; mark yourself paid below.</p>
          </div>
        </div>
        <Badge
          className={`text-[10px] uppercase tracking-wider border ${pill.cls}`}
          data-testid="pay-status-pill"
        >
          {pill.label}
        </Badge>
      </div>

      {/* Bank fields (only shown if any are present). */}
      {(bank.account_name || bank.sort_code || bank.account_number) && (
        <div className="space-y-2" data-testid="pay-bank-block">
          {bank.account_name && (
            <CopyRow label="Account name" value={bank.account_name} testid="pay-copy-account-name" />
          )}
          <div className="grid grid-cols-2 gap-2">
            {bank.sort_code && (
              <CopyRow label="Sort code" value={bank.sort_code} testid="pay-copy-sort-code" />
            )}
            {bank.account_number && (
              <CopyRow label="Account number" value={bank.account_number} testid="pay-copy-account-number" />
            )}
          </div>
          {bank.reference_instruction && (
            <CopyRow label="Use as reference" value={bank.reference_instruction} testid="pay-copy-reference" />
          )}
        </div>
      )}

      {bank.payment_notes && (
        <div className="rounded-xl border border-border/30 bg-muted/20 p-3" data-testid="pay-notes-block">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Notes from organiser</p>
          <p className="text-xs whitespace-pre-line">{bank.payment_notes}</p>
        </div>
      )}

      {cashAccepted && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-400/30 text-xs text-amber-200" data-testid="pay-cash-accepted-banner">
          <Banknote className="w-3.5 h-3.5" />
          Cash on the night also accepted.
        </div>
      )}

      {/* Action row */}
      {!isOrganiser && (
        <div className="flex flex-col gap-2 pt-2 border-t border-border/30">
          {status === 'received' ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-sm font-semibold" data-testid="pay-received-banner">
              <CheckCircle2 className="w-4 h-4" /> Organiser confirmed your payment
            </div>
          ) : status === 'refunded' ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-sm font-semibold" data-testid="pay-refunded-banner">
              Refunded by organiser
            </div>
          ) : status === 'marked_paid' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-400/30 text-blue-200 text-xs" data-testid="pay-waiting-banner">
                <Clock className="w-3.5 h-3.5" />
                <span className="flex-1">Waiting for organiser to confirm</span>
              </div>
              {proof && (
                <p className="text-[11px] text-muted-foreground px-1">
                  You marked paid {proof.paid_date}{proof.paid_time ? ` at ${proof.paid_time}` : ''}
                  {proof.reference ? ` · ref “${proof.reference}”` : ''} · {(proof.method || '').replace('_', ' ')}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full rounded-full"
                onClick={() => setShowPaidDialog(true)}
                data-testid="pay-update-proof-btn"
              >
                Update payment proof
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              className="w-full rounded-full h-11 gap-2"
              onClick={() => setShowPaidDialog(true)}
              disabled={loading}
              data-testid="pay-ive-paid-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              I&apos;ve paid
            </Button>
          )}
        </div>
      )}

      <IvePaidDialog
        open={showPaidDialog}
        onOpenChange={setShowPaidDialog}
        eventId={eventId}
        cashAccepted={cashAccepted}
        existingProof={proof}
        onSaved={() => {
          setShowPaidDialog(false);
          refresh();
          onPaymentChanged?.();
        }}
      />
    </div>
  );
}

function IvePaidDialog({ open, onOpenChange, eventId, cashAccepted, existingProof, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [paidDate, setPaidDate] = useState(existingProof?.paid_date || today);
  const [paidTime, setPaidTime] = useState(existingProof?.paid_time || '');
  const [reference, setReference] = useState(existingProof?.reference || '');
  const [method, setMethod] = useState(existingProof?.method || 'bank_transfer');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPaidDate(existingProof?.paid_date || today);
      setPaidTime(existingProof?.paid_time || '');
      setReference(existingProof?.reference || '');
      setMethod(existingProof?.method || 'bank_transfer');
    }
  }, [open, existingProof]);

  const submit = async () => {
    if (!paidDate) {
      toast.error('Please enter the date you paid');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/mark-paid`,
        { paid_date: paidDate, paid_time: paidTime, reference, method },
        { headers: authHeaders() }
      );
      toast.success("Marked as paid — organiser will confirm shortly");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save payment');
    } finally {
      setSubmitting(false);
    }
  };

  const methods = [
    { key: 'bank_transfer', label: 'Bank transfer' },
    { key: 'paypal', label: 'PayPal' },
    ...(cashAccepted ? [{ key: 'cash', label: 'Cash' }] : []),
    { key: 'other', label: 'Other' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-ive-paid-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as paid</DialogTitle>
          <DialogDescription>
            Drop a quick proof so the organiser can spot your transfer and confirm it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date paid *</Label>
              <Input
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="pay-dialog-date-input"
              />
            </div>
            <div>
              <Label className="text-xs">Time (optional)</Label>
              <Input
                type="time"
                value={paidTime}
                onChange={(e) => setPaidTime(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="pay-dialog-time-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Method</Label>
            <div className="grid grid-cols-2 gap-2">
              {methods.map(m => (
                <Button
                  key={m.key}
                  type="button"
                  variant={method === m.key ? 'default' : 'outline'}
                  className="h-10 rounded-xl text-xs"
                  onClick={() => setMethod(m.key)}
                  data-testid={`pay-dialog-method-${m.key}`}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Reference / last 4 digits (optional)</Label>
            <Input
              placeholder="e.g. Skate Night Jane S"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="pay-dialog-reference-input"
            />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-full gap-2"
            onClick={submit}
            disabled={submitting}
            data-testid="pay-dialog-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Mark as paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
